import { getAvailableResult } from "DependencyResult";
import type {
  DependencyAccessMixedValue,
  DependencyAccessValue,
  DependencyArgument,
  DependencyVisitorContext,
} from "DependencyVisitorContext";
import { State } from "StateConstants";

export function areValuesEqual(a: unknown, b: unknown): boolean {
  if (a == null) {
    return b == null;
  } else if (b == null) {
    return false;
  } else {
    return a.valueOf() === b.valueOf();
  }
}

export function decoratedWaitAvailable<Mode, Data, In, Out>(
  args: DependencyArgument<Data, In>[],
  data: Data,
  context: DependencyVisitorContext<Mode>,
  callback: (value: (In | null | undefined)[]) => DependencyAccessMixedValue<Mode, Out>,
  decorationInfo: unknown,
): DependencyAccessValue<Mode, Out> {
  return context.strategy.waitAvailable(args, data, context, decorateCallback(context, callback, decorationInfo));
}

export function decoratedWaitAvailableOne<Mode, Data, In, Out>(
  args: DependencyArgument<Data, In>,
  data: Data,
  context: DependencyVisitorContext<Mode>,
  callback: (value: In) => DependencyAccessMixedValue<Mode, Out>,
  decorationInfo: unknown,
): DependencyAccessMixedValue<Mode, Out> {
  return context.strategy.waitAvailableOne(args, data, context, decorateCallback(context, callback, decorationInfo));
}

export function mapData<Mode, Data, In, Out>(
  args: DependencyArgument<Data, In>[],
  data: Data,
  context: DependencyVisitorContext<Mode>,
  mapper: (value: (In | null | undefined)[]) => DependencyAccessMixedValue<Mode, Out>,
  decorationInfo?: unknown,
): MappedReturnType<Mode, Out> {
  return mapDataCore(context.strategy.waitAvailable, args, data, context, mapper, decorationInfo);
}

export function mapDataOne<Mode, Data, In, Out>(
  arg: DependencyArgument<Data, In>,
  data: Data,
  context: DependencyVisitorContext<Mode>,
  mapper: (value: In) => DependencyAccessMixedValue<Mode, Out>,
  decorationInfo?: unknown,
): MappedReturnType<Mode, Out> {
  return mapDataCore(context.strategy.waitAvailableOne, arg, data, context, mapper, decorationInfo);
}

function mapDataCore<Mode, Arg, Data, In, Out>(
  wait: (
    arg: Arg,
    data: Data,
    context: DependencyVisitorContext<Mode>,
    callback: (value: In) => DependencyAccessMixedValue<Mode, Out>,
  ) => DependencyAccessMixedValue<Mode, Out>,
  arg: Arg,
  data: Data,
  context: DependencyVisitorContext<Mode>,
  mapper: (value: In) => DependencyAccessMixedValue<Mode, Out>,
  decorationInfo?: unknown,
): MappedReturnType<Mode, Out> {
  mapper = decorateCallback(context, mapper, decorationInfo);

  if (Array.isArray(data)) {
    return context.strategy.map(
      data,
      (item) => wait(arg, item, context, mapper),
      (items) => {
        const result: Out[] = [];
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (item.state === State.NotLoaded) {
            return item;
          } else if (item.state === State.Available) {
            result.push(item.value);
          }
        }
        return getAvailableResult(result);
      },
    );
  } else {
    return wait(arg, data, context, mapper);
  }
}

function decorateCallback<Mode, In, Out>(
  context: DependencyVisitorContext<Mode>,
  callback: (value: In) => DependencyAccessMixedValue<Mode, Out>,
  decorationInfo?: unknown,
): (value: In) => DependencyAccessMixedValue<Mode, Out> {
  if (context.options.decorate) {
    return context.options.decorate(callback, decorationInfo);
  }
  return callback;
}

export const operationTypes = Object.freeze({
  Boolean: "Boolean",
  NotEquals: "NotEquals",
  Comparison: "Comparison",
  Method: "Method",
});

export type MappedReturnType<Mode, T> = DependencyAccessMixedValue<Mode, T> | DependencyAccessMixedValue<Mode, T[]>;
